export const SESSION_FOREGROUND_OWNERSHIP_BROADCAST_CHANNEL = "cord-session-foreground-ownership"
const SESSION_FOREGROUND_LEASE_STORAGE_PREFIX = "cord:session-foreground-lease:"
// Heartbeat every two seconds so other tabs learn about active ownership quickly
// without creating noisy cross-tab traffic. This may still feel slow if we later
// need sub-second ownership transfer for extremely rapid tab thrash.
const FOREGROUND_LEASE_HEARTBEAT_MS = 2_000
// Allow a little over three missed heartbeats before expiring a lease.
const FOREGROUND_LEASE_TTL_MS = 7_000
let cachedForegroundOwnershipTabId: string | null = null

export interface SessionForegroundLease {
  scopeKey: string
  sessionId: string
  tabId: string
  claimedAt: number
  expiresAt: number
}

interface SessionForegroundLeaseUpsertMessage {
  type: "lease_upsert"
  lease: SessionForegroundLease
}

interface SessionForegroundLeaseReleaseMessage {
  type: "lease_release"
  scopeKey: string
  sessionId: string
  tabId: string
}

type SessionForegroundOwnershipMessage =
  | SessionForegroundLeaseUpsertMessage
  | SessionForegroundLeaseReleaseMessage

const knownLeases = new Map<string, SessionForegroundLease>()
const subscribers = new Set<() => void>()
let broadcastChannel: BroadcastChannel | null = null
let initialized = false
let leaseExpiryTimer: ReturnType<typeof setTimeout> | null = null

function getCurrentTimestamp(): number {
  return Date.now()
}

function normalizePositiveInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null
  return Math.floor(value)
}

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function buildForegroundOwnershipTabId(): string {
  if (cachedForegroundOwnershipTabId) {
    return cachedForegroundOwnershipTabId
  }

  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    cachedForegroundOwnershipTabId = crypto.randomUUID()
    return cachedForegroundOwnershipTabId
  }

  cachedForegroundOwnershipTabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return cachedForegroundOwnershipTabId
}

function composeLeaseKey(args: {
  scopeKey: string
  sessionId: string
  tabId: string
}): string {
  return `${args.scopeKey}:${args.sessionId}:${args.tabId}`
}

function composeLeaseStorageKey(args: {
  scopeKey: string
  sessionId: string
  tabId: string
}): string {
  return `${SESSION_FOREGROUND_LEASE_STORAGE_PREFIX}${encodeURIComponent(args.scopeKey)}:${encodeURIComponent(args.sessionId)}:${encodeURIComponent(args.tabId)}`
}

function parseLeaseStorageKey(key: string): {
  scopeKey: string
  sessionId: string
  tabId: string
} | null {
  const keyWithoutPrefix = key.slice(SESSION_FOREGROUND_LEASE_STORAGE_PREFIX.length)
  const [encodedScopeKey, encodedSessionId, encodedTabId] = keyWithoutPrefix.split(":")
  if (!encodedScopeKey || !encodedSessionId || !encodedTabId) return null
  try {
    return {
      scopeKey: decodeURIComponent(encodedScopeKey),
      sessionId: decodeURIComponent(encodedSessionId),
      tabId: decodeURIComponent(encodedTabId),
    }
  } catch {
    return null
  }
}

function isSessionForegroundLease(value: unknown): value is SessionForegroundLease {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const raw = value as Record<string, unknown>
  return (
    normalizeNonEmptyString(raw.scopeKey) !== null &&
    normalizeNonEmptyString(raw.sessionId) !== null &&
    normalizeNonEmptyString(raw.tabId) !== null &&
    normalizePositiveInteger(raw.claimedAt) !== null &&
    normalizePositiveInteger(raw.expiresAt) !== null
  )
}

function normalizeLease(value: unknown): SessionForegroundLease | null {
  if (!isSessionForegroundLease(value)) return null
  return {
    scopeKey: value.scopeKey.trim(),
    sessionId: value.sessionId.trim(),
    tabId: value.tabId.trim(),
    claimedAt: value.claimedAt,
    expiresAt: value.expiresAt,
  }
}

function isOwnershipMessage(value: unknown): value is SessionForegroundOwnershipMessage {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const raw = value as Record<string, unknown>
  if (raw.type === "lease_upsert") {
    return normalizeLease(raw.lease) !== null
  }
  if (raw.type === "lease_release") {
    return (
      normalizeNonEmptyString(raw.scopeKey) !== null &&
      normalizeNonEmptyString(raw.sessionId) !== null &&
      normalizeNonEmptyString(raw.tabId) !== null
    )
  }
  return false
}

function purgeExpiredLeases(now = getCurrentTimestamp()): boolean {
  let changed = false
  for (const [key, lease] of knownLeases.entries()) {
    if (lease.expiresAt > now) continue
    knownLeases.delete(key)
    changed = true
  }
  return changed
}

export function getNextForegroundLeaseExpiryAt(args: {
  leases: Iterable<SessionForegroundLease>
  now?: number
}): number | null {
  const now = args.now ?? getCurrentTimestamp()
  let nextExpiryAt: number | null = null
  for (const lease of args.leases) {
    if (lease.expiresAt <= now) continue
    if (nextExpiryAt === null || lease.expiresAt < nextExpiryAt) {
      nextExpiryAt = lease.expiresAt
    }
  }
  return nextExpiryAt
}

function scheduleLeaseExpiryNotification(): void {
  if (leaseExpiryTimer) {
    clearTimeout(leaseExpiryTimer)
    leaseExpiryTimer = null
  }

  const nextExpiryAt = getNextForegroundLeaseExpiryAt({
    leases: knownLeases.values(),
  })
  if (nextExpiryAt === null) return

  const delayMs = Math.max(0, nextExpiryAt - getCurrentTimestamp() + 1)
  leaseExpiryTimer = setTimeout(() => {
    leaseExpiryTimer = null
    if (purgeExpiredLeases()) {
      notifySubscribers()
      return
    }
    scheduleLeaseExpiryNotification()
  }, delayMs)
}

function notifySubscribers(): void {
  purgeExpiredLeases()
  for (const listener of subscribers) {
    listener()
  }
  scheduleLeaseExpiryNotification()
}

function mirrorLeaseToStorage(lease: SessionForegroundLease): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  try {
    window.localStorage.setItem(
      composeLeaseStorageKey(lease),
      JSON.stringify(lease),
    )
  } catch {
    // Ignore storage failures; BroadcastChannel still propagates ownership.
  }
}

function removeLeaseFromStorage(args: {
  scopeKey: string
  sessionId: string
  tabId: string
}): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  try {
    window.localStorage.removeItem(composeLeaseStorageKey(args))
  } catch {
    // Ignore storage failures.
  }
}

function postOwnershipMessage(message: SessionForegroundOwnershipMessage): void {
  try {
    broadcastChannel?.postMessage(message)
  } catch {
    // Ignore broadcast failures.
  }
}

function upsertKnownLease(lease: SessionForegroundLease): void {
  knownLeases.set(composeLeaseKey(lease), lease)
}

function removeKnownLease(args: {
  scopeKey: string
  sessionId: string
  tabId: string
}): void {
  knownLeases.delete(composeLeaseKey(args))
}

function handleStorageEvent(event: StorageEvent): void {
  if (!event.key || !event.key.startsWith(SESSION_FOREGROUND_LEASE_STORAGE_PREFIX)) return
  if (event.newValue) {
    try {
      const parsed = JSON.parse(event.newValue) as unknown
      const lease = normalizeLease(parsed)
      if (!lease) return
      upsertKnownLease(lease)
      notifySubscribers()
    } catch {
      // Ignore malformed mirrored leases.
    }
    return
  }

  const parsedKey = parseLeaseStorageKey(event.key)
  if (!parsedKey) return
  removeKnownLease(parsedKey)
  notifySubscribers()
}

function handleBroadcastMessage(event: MessageEvent<unknown>): void {
  const message = event.data
  if (!isOwnershipMessage(message)) return
  if (message.type === "lease_upsert") {
    const lease = normalizeLease(message.lease)
    if (!lease) return
    upsertKnownLease(lease)
    notifySubscribers()
    return
  }

  removeKnownLease({
    scopeKey: message.scopeKey,
    sessionId: message.sessionId,
    tabId: message.tabId,
  })
  notifySubscribers()
}

function bootstrapMirroredLeases(): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return
  const now = getCurrentTimestamp()
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index)
    if (!key || !key.startsWith(SESSION_FOREGROUND_LEASE_STORAGE_PREFIX)) continue
    try {
      const raw = window.localStorage.getItem(key)
      if (!raw) continue
      const lease = normalizeLease(JSON.parse(raw) as unknown)
      if (!lease || lease.expiresAt <= now) {
        window.localStorage.removeItem(key)
        continue
      }
      upsertKnownLease(lease)
    } catch {
      // Ignore malformed bootstrap entries.
    }
  }
}

function initializeForegroundOwnership(): void {
  if (initialized || typeof window === "undefined") return
  initialized = true
  bootstrapMirroredLeases()
  scheduleLeaseExpiryNotification()
  if (typeof window.BroadcastChannel !== "undefined") {
    try {
      broadcastChannel = new window.BroadcastChannel(
        SESSION_FOREGROUND_OWNERSHIP_BROADCAST_CHANNEL,
      )
      broadcastChannel.addEventListener("message", handleBroadcastMessage)
    } catch {
      broadcastChannel = null
    }
  }
  window.addEventListener("storage", handleStorageEvent)
}

export function getForegroundOwnershipTabId(): string {
  return buildForegroundOwnershipTabId()
}

export function collapseForegroundLeasesBySession(args: {
  leases: Iterable<SessionForegroundLease>
  scopeKey: string | null
}): Record<string, SessionForegroundLease> {
  if (!args.scopeKey) return {}
  const next: Record<string, SessionForegroundLease> = {}
  for (const lease of args.leases) {
    if (lease.scopeKey !== args.scopeKey) continue
    const existing = next[lease.sessionId]
    if (
      !existing ||
      lease.expiresAt > existing.expiresAt ||
      (lease.expiresAt === existing.expiresAt && lease.claimedAt > existing.claimedAt)
    ) {
      next[lease.sessionId] = lease
    }
  }
  return next
}

export function getForegroundSessionLeaseSnapshot(
  scopeKey: string | null,
): Record<string, SessionForegroundLease> {
  initializeForegroundOwnership()
  purgeExpiredLeases()
  return collapseForegroundLeasesBySession({
    leases: knownLeases.values(),
    scopeKey,
  })
}

export function subscribeForegroundSessionLeaseSnapshot(
  listener: () => void,
): () => void {
  initializeForegroundOwnership()
  subscribers.add(listener)
  return () => {
    subscribers.delete(listener)
  }
}

export function isSessionForegroundOwnedElsewhere(args: {
  snapshot: Record<string, SessionForegroundLease>
  sessionId: string
  currentTabId: string
}): boolean {
  const lease = args.snapshot[args.sessionId]
  if (!lease) return false
  return lease.tabId !== args.currentTabId
}

export function isSessionForegroundOwnedByCurrentTab(args: {
  snapshot: Record<string, SessionForegroundLease>
  sessionId: string
  currentTabId: string
}): boolean {
  const lease = args.snapshot[args.sessionId]
  if (!lease) return false
  return lease.tabId === args.currentTabId
}

export function isDocumentEligibleForForegroundSessionLease(args: {
  visibilityState: DocumentVisibilityState | null | undefined
}): boolean {
  // Users often keep a chat window visible beside their main work window. A
  // visible-but-blurred document should keep the foreground sync lease so the
  // active thread continues to stream while focus is in another application.
  return args.visibilityState === "visible"
}

function isDocumentForeground(): boolean {
  if (typeof document === "undefined") return false
  return isDocumentEligibleForForegroundSessionLease({
    visibilityState: document.visibilityState,
  })
}

export function shouldReleaseForegroundLeaseOnWindowBlur(args: {
  visibilityState: DocumentVisibilityState | null | undefined
}): boolean {
  return args.visibilityState === "hidden"
}

function getCurrentSessionOwner(args: {
  scopeKey: string
  sessionId: string
}): SessionForegroundLease | null {
  const snapshot = collapseForegroundLeasesBySession({
    leases: knownLeases.values(),
    scopeKey: args.scopeKey,
  })
  return snapshot[args.sessionId] ?? null
}

export function claimForegroundSessionLease(args: {
  scopeKey: string | null
  sessionId: string
  // Keep takeover opt-in so a booting tab does not steal an active tab's
  // foreground transport before the new tab has enough session state to sync.
  allowExistingOwnerTakeover?: boolean
}): () => void {
  initializeForegroundOwnership()
  if (typeof document === "undefined" || !args.scopeKey || !args.sessionId) {
    return () => {}
  }

  const tabId = getForegroundOwnershipTabId()
  let released = false

  const publishLease = (options?: { force?: boolean }) => {
    if (released || !isDocumentForeground()) return
    const now = getCurrentTimestamp()
    const currentOwner = getCurrentSessionOwner({
      scopeKey: args.scopeKey!,
      sessionId: args.sessionId,
    })
    const forceTakeover = options?.force === true && args.allowExistingOwnerTakeover === true
    if (
      !forceTakeover &&
      currentOwner &&
      currentOwner.tabId !== tabId &&
      currentOwner.expiresAt > now
    ) {
      return
    }
    const lease: SessionForegroundLease = {
      scopeKey: args.scopeKey!,
      sessionId: args.sessionId,
      tabId,
      claimedAt: now,
      expiresAt: now + FOREGROUND_LEASE_TTL_MS,
    }
    upsertKnownLease(lease)
    mirrorLeaseToStorage(lease)
    postOwnershipMessage({ type: "lease_upsert", lease })
    notifySubscribers()
  }

  const releaseLease = () => {
    removeKnownLease({
      scopeKey: args.scopeKey!,
      sessionId: args.sessionId,
      tabId,
    })
    removeLeaseFromStorage({
      scopeKey: args.scopeKey!,
      sessionId: args.sessionId,
      tabId,
    })
    postOwnershipMessage({
      type: "lease_release",
      scopeKey: args.scopeKey!,
      sessionId: args.sessionId,
      tabId,
    })
    notifySubscribers()
  }

  const handleVisibilityChange = () => {
    if (isDocumentForeground()) {
      publishLease({ force: true })
      return
    }
    releaseLease()
  }

  const handleWindowFocus = () => {
    publishLease({ force: true })
  }

  const handleWindowBlur = () => {
    if (shouldReleaseForegroundLeaseOnWindowBlur({
      visibilityState: typeof document === "undefined" ? null : document.visibilityState,
    })) {
      releaseLease()
    }
  }

  const heartbeatTimer = setInterval(() => {
    if (!isDocumentForeground()) return
    publishLease()
  }, FOREGROUND_LEASE_HEARTBEAT_MS)

  publishLease({ force: true })
  document.addEventListener("visibilitychange", handleVisibilityChange)
  window.addEventListener("focus", handleWindowFocus)
  window.addEventListener("blur", handleWindowBlur)
  window.addEventListener("pagehide", releaseLease)

  return () => {
    if (released) return
    released = true
    clearInterval(heartbeatTimer)
    document.removeEventListener("visibilitychange", handleVisibilityChange)
    window.removeEventListener("focus", handleWindowFocus)
    window.removeEventListener("blur", handleWindowBlur)
    window.removeEventListener("pagehide", releaseLease)
    releaseLease()
  }
}
